/**
 * 활동 로그 description의 `| details=` JSON에서 변경 필드(before/after) 추출.
 * 서버가 memo를 `changed` 밖에 두거나 한글 키로 줄 때도 UI/엑셀에서 동일하게 보도록 정규화.
 */

export type ChangedFieldMap = Record<string, { before: unknown; after: unknown }>;

function isBeforeAfterShape(v: unknown): v is { before?: unknown; after?: unknown } {
  return Boolean(v && typeof v === 'object' && ('before' in v || 'after' in v));
}

function mergeMemoFromSnapshot(out: ChangedFieldMap, slotSnapshot?: Record<string, unknown> | null): void {
  if (!slotSnapshot || out.memo) return;
  const b =
    slotSnapshot.memo_before ??
    slotSnapshot.memoBefore ??
    slotSnapshot.before_memo ??
    slotSnapshot.beforeMemo;
  const a =
    slotSnapshot.memo_after ??
    slotSnapshot.memoAfter ??
    slotSnapshot.after_memo ??
    slotSnapshot.afterMemo;
  if (b !== undefined || a !== undefined) {
    out.memo = { before: b ?? '-', after: a ?? '-' };
  }
}

/**
 * @param description 로그 description 전체 문자열
 * @param slotSnapshot 선택. memo 전용 스냅샷 필드가 있으면 `changed`에 memo가 없을 때 보강
 */
export function extractChangedFieldsFromLogDescription(
  description: string | null | undefined,
  slotSnapshot?: Record<string, unknown> | null,
): ChangedFieldMap {
  const out: ChangedFieldMap = {};

  if (description) {
    const idx = description.indexOf('| details=');
    if (idx !== -1) {
      const jsonPart = description.slice(idx + '| details='.length).trim();
      try {
        const details = JSON.parse(jsonPart) as Record<string, unknown>;
        const changed = details?.changed;
        if (changed && typeof changed === 'object') {
          for (const [k, v] of Object.entries(changed as Record<string, unknown>)) {
            if (!isBeforeAfterShape(v)) continue;
            const key = k === '메모' ? 'memo' : k;
            out[key] = { before: v.before, after: v.after };
          }
        }
        const hoist = (node: unknown) => {
          if (!isBeforeAfterShape(node)) return;
          if (!out.memo) out.memo = { before: node.before, after: node.after };
        };
        hoist(details.memo);
        hoist(details.메모);
      } catch {
        // 무시
      }
    }
  }

  mergeMemoFromSnapshot(out, slotSnapshot ?? null);
  return out;
}
